import React, { useEffect } from 'react';
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Language as LanguageIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useConnection } from '@/app/contexts/ConnectionContext';
import { useAppLocale } from '@/app/permissions/useAppLocale';
import { shouldSyncChromeLocale } from '@/app/permissions/syncChromeLocale';
import { runtimeApi } from '@/app/services/runtimeApi';

export interface LanguageSwitcherProps {
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ size = 'medium', sx }) => {
  const { t, i18n } = useTranslation();
  const { selectedConnection } = useConnection();
  const { locale, languages, loading } = useAppLocale();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (loading) return
    const next = shouldSyncChromeLocale(locale, i18n.language)
    if (!next) return
    void i18n.changeLanguage(next)
    localStorage.setItem('i18nextLng', next)
  }, [loading, locale, i18n])
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: string) => {
    handleClose();
    const connectionId = selectedConnection?.id;
    if (!connectionId) return;
    try {
      await runtimeApi.setMyLocale(connectionId, languageCode);
      queryClient.invalidateQueries(['my-permissions', connectionId]);
    } catch (e: any) {
      // ApiService throws a plain { detail } object — same as form save.
      setError(e?.detail || e?.response?.data?.detail || e?.message || 'Failed to set language');
    }
  };

  if (languages.length < 2) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('language.select')}>
        <IconButton
          onClick={handleClick}
          color="inherit"
          size={size}
          sx={sx}
          aria-label="select language"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={locale === language.code}
          >
            <ListItemIcon>
              {locale === language.code && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={language.name} />
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error ?? ''}
      />
    </>
  );
};
